// v2 bush — a berry bush: a pair of woody stems under a layered dome of flattened
// leaf lobes (a skirt ring of four under a crown), dotted with six bright berries
// sitting proud of the canopy. Variants re-roll the berry colour (red /
// rowan-orange / elder-purple), shift the foliage tone and turn the whole clump.
// Ported 1:1 from age-of-mcp's v2/bush.ts, using the library's shared
// `groundcover` (h01, lerp) helpers.
import * as THREE from "three";
import { defineModel, groundcover, type Part } from "../../lib";

const TAU = Math.PI * 2;
const BERRY = ["#d9382e", "#e07424", "#6c3f9e"];
const LEAF: [string, string, string][] = [
  ["#37692e", "#447e38", "#549444"],
  ["#3d7030", "#4c873a", "#5f9e48"],
  ["#2f6029", "#3b7433", "#4a8a3e"],
];

function bushParts(v: number): Part[] {
  const seed = v * 13 + 7;
  const yaw = v * 2.1;
  const [dark, mid, light] = LEAF[v % LEAF.length]!;
  const parts: Part[] = [
    // woody stems the canopy sits on
    { geometry: new THREE.CylinderGeometry(0.012, 0.018, 0.1, 5),
      color: "#5e4630", position: [0.02, 0.05, 0.01], rotation: [0.1, 0, -0.14] },
    { geometry: new THREE.CylinderGeometry(0.009, 0.014, 0.085, 5),
      color: "#54402c", position: [-0.035, 0.042, -0.015], rotation: [-0.08, 0, 0.2] },
  ];
  // skirt — a ring of four flattened leaf lobes
  for (let i = 0; i < 4; i++) {
    const a = yaw + (i / 4) * TAU + groundcover.h01(seed, i) * 0.5;
    const r = groundcover.lerp(0.062, 0.082, groundcover.h01(seed, i + 4));
    parts.push({
      geometry: new THREE.IcosahedronGeometry(groundcover.lerp(0.075, 0.09, groundcover.h01(seed, i + 8)), 0),
      color: i % 2 ? mid : dark,
      position: [Math.cos(a) * r, groundcover.lerp(0.105, 0.13, groundcover.h01(seed, i + 12)), Math.sin(a) * r],
      rotation: [0, a, 0],
      scale: [1.2, 0.72, 1.15],
    });
  }
  // crown lobe
  parts.push({
    geometry: new THREE.IcosahedronGeometry(0.075, 0), color: light,
    position: [0.005, 0.2, -0.005], rotation: [0, yaw, 0], scale: [1.1, 0.75, 1.05],
  });
  // berries dotted over the canopy
  for (let i = 0; i < 6; i++) {
    const a = yaw + (i / 6) * TAU + groundcover.h01(seed, i + 16) * 0.9;
    const r = groundcover.lerp(0.07, 0.105, groundcover.h01(seed, i + 22));
    parts.push({
      geometry: new THREE.IcosahedronGeometry(0.016, 0),
      color: BERRY[v % BERRY.length]!,
      position: [Math.cos(a) * r, groundcover.lerp(0.13, 0.21, groundcover.h01(seed, i + 28)), Math.sin(a) * r],
    });
  }
  return parts;
}

export const bushV2 = defineModel({
  id: "bush-v2",
  batchId: "fable",
  name: "Bush (v2)",
  tags: ["scenery","bush","low-poly","age-of-mcp","fable"],
  summary: "Low-poly bush.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.2,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => bushParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => bushParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => bushParts(2) },
  ],
  animations: [],
});
